import { useState } from 'react';

const QuoteSidebar = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.service) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Create WhatsApp message
      const whatsappMessage = `*New Removals Quote Request*%0A%0A*Name:* ${encodeURIComponent(formData.name)}%0A*Phone:* ${encodeURIComponent(formData.phone)}%0A*Service:* ${encodeURIComponent(formData.service)}${formData.message ? `%0A*Details:* ${encodeURIComponent(formData.message)}` : ''}`;
      
      // Open WhatsApp with the message
      window.open(`https://wa.me/27798768956?text=${whatsappMessage}`, '_blank');
      
      setSubmitStatus('success');
      setFormData({ name: '', phone: '', service: '', message: '' });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="lg:sticky lg:top-24">
      <div className="bg-primary rounded-2xl p-8 text-white mb-6">
        <h3 className="font-bold text-2xl mb-4">Request a Free Quote</h3>
        <p className="text-white/90 mb-6">
          Get a transparent, no-obligation quote for your bakkie removal needs
        </p>

        <form onSubmit={handleSubmit} data-readdy-form id="removals-quote-form">
          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium mb-2">
                Your Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 rounded-lg text-charcoal focus:outline-none focus:ring-2 focus:ring-white"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 rounded-lg text-charcoal focus:outline-none focus:ring-2 focus:ring-white"
                placeholder="079 876 8956"
              />
            </div>

            <div>
              <label htmlFor="service" className="block text-sm font-medium mb-2">
                Service Needed *
              </label>
              <select
                id="service"
                name="service"
                value={formData.service}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 rounded-lg text-charcoal focus:outline-none focus:ring-2 focus:ring-white"
              >
                <option value="">Select a service</option>
                <option value="Local Home Removals">Local Home Removals</option>
                <option value="Office Furniture Transport">Office Furniture Transport</option>
                <option value="Long-Distance Removals">Long-Distance Removals</option>
                <option value="Furniture Transport">Furniture Transport</option>
                <option value="Appliance Transport">Appliance Transport</option>
                <option value="Garden Waste Removal">Garden Waste Removal</option>
              </select>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium mb-2">
                Additional Details
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                maxLength={500}
                rows={4}
                className="w-full px-4 py-3 rounded-lg text-charcoal focus:outline-none focus:ring-2 focus:ring-white resize-none"
                placeholder="Tell us about your removal needs..."
              ></textarea>
              <p className="text-xs text-white/70 mt-1">{formData.message.length}/500 characters</p>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-white text-primary px-6 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? 'Opening WhatsApp...' : 'Send Quote via WhatsApp'}
            </button>

            {submitStatus === 'success' && (
              <div className="bg-white/20 border border-white/40 rounded-lg p-4 text-center">
                <i className="ri-checkbox-circle-line text-3xl mb-2 w-8 h-8 flex items-center justify-center mx-auto"></i>
                <p className="font-semibold">WhatsApp opened successfully!</p>
                <p className="text-sm text-white/90 mt-1">Please send the message to complete your quote request.</p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-500/20 border border-red-300 rounded-lg p-4 text-center">
                <p className="font-semibold">Please fill in all required fields</p>
                <p className="text-sm text-white/90 mt-1">Name, phone, and service are required.</p>
              </div>
            )}
          </div>
        </form>
      </div>

      <div className="bg-greenbg rounded-2xl p-6">
        <h4 className="font-bold text-xl text-charcoal mb-4">Contact Information</h4>
        <div className="space-y-4">
          <a
            href="tel:+27798768956"
            className="flex items-center space-x-3 text-gray-700 hover:text-primary transition-colors cursor-pointer"
          >
            <i className="ri-phone-line text-primary text-xl w-6 h-6 flex items-center justify-center"></i>
            <span className="font-medium">079 876 8956</span>
          </a>
          <a
            href="mailto:bookings@lookwhosmoving.co.za"
            className="flex items-center space-x-3 text-gray-700 hover:text-primary transition-colors cursor-pointer"
          >
            <i className="ri-mail-line text-primary text-xl w-6 h-6 flex items-center justify-center"></i>
            <span className="font-medium">bookings@lookwhosmoving.co.za</span>
          </a>
          <a
            href="https://wa.me/27798768956"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-3 text-gray-700 hover:text-primary transition-colors cursor-pointer"
          >
            <i className="ri-whatsapp-line text-primary text-xl w-6 h-6 flex items-center justify-center"></i>
            <span className="font-medium">WhatsApp Us</span>
          </a>
          <div className="flex items-start space-x-3 text-gray-700">
            <i className="ri-map-pin-line text-primary text-xl mt-1 w-6 h-6 flex items-center justify-center"></i>
            <span className="font-medium">Pinetown, Durban, KwaZulu-Natal</span>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-gray-300">
          <div className="flex items-center space-x-2 mb-3">
            <i className="ri-shield-check-line text-primary text-xl w-6 h-6 flex items-center justify-center"></i>
            <span className="font-semibold text-charcoal">Fully Insured</span>
          </div>
          <div className="flex items-center space-x-2 mb-3">
            <i className="ri-time-line text-primary text-xl w-6 h-6 flex items-center justify-center"></i>
            <span className="font-semibold text-charcoal">Same-Day Available</span>
          </div>
          <div className="flex items-center space-x-2">
            <i className="ri-price-tag-3-line text-primary text-xl w-6 h-6 flex items-center justify-center"></i>
            <span className="font-semibold text-charcoal">No Hidden Fees</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteSidebar;
